import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { customEventsApi } from '../../api/customEvents';

// "What has this event actually done?" under the builder (DWLF-305; canvas board BuildEvent):
// the saved event's fires over the last two years, one row per symbol, from the API's event store.
// It reflects the SAVED definition — the strip says so — and computes no performance figure.

const DAYS = 730;
const MAX_PAGES = 10;
const MAX_ROWS = 4;
const W = 1000;
const ROW_H = 22;
const tok = (t) => `var(--dwlf-${t})`;
const LABEL = { fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.12em' };
const MONO = '"IBM Plex Mono", monospace';
const day = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });

export function summariseFires(fires, now = Date.now()) {
  const t0 = now - DAYS * 864e5;
  const list = (fires || [])
    .map((f) => ({ ...f, t: new Date(f.eventTimestamp || f.date).getTime() }))
    .filter((f) => Number.isFinite(f.t) && f.t >= t0)
    .sort((a, b) => a.t - b.t);
  const bySymbol = new Map();
  list.forEach((f) => { if (!bySymbol.has(f.symbol)) bySymbol.set(f.symbol, []); bySymbol.get(f.symbol).push(f); });
  // Gaps between consecutive fires on the SAME symbol: pooling symbols would interleave their fires
  // and shrink the gap toward zero.
  const gaps = [...bySymbol.values()]
    .flatMap((fs) => fs.slice(1).map((f, i) => (f.t - fs[i].t) / 864e5))
    .sort((a, b) => a - b);
  const medianGapDays = gaps.length ? gaps[Math.floor(gaps.length / 2)] : null;
  return { t0, t1: now, count: list.length, last: list[list.length - 1] || null, medianGapDays, rows: [...bySymbol.entries()].slice(0, MAX_ROWS), more: Math.max(0, bySymbol.size - MAX_ROWS) };
}

export default function EventFiresStrip({ eventId }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let live = true;
    setState({ loading: true });
    // Every page of the window, following the API's cursor, up to MAX_PAGES (then it says so).
    (async () => {
      const fires = [];
      let cursor;
      let pages = 0;
      do {
        // eslint-disable-next-line no-await-in-loop -- pages are sequential by nature (cursor)
        const d = await customEventsApi.getHistoricalFires(eventId, DAYS, 200, cursor);
        fires.push(...(d?.events || []));
        cursor = d?.cursor || null;
        pages += 1;
      } while (cursor && pages < MAX_PAGES);
      return { fires, truncated: Boolean(cursor) };
    })()
      .then(({ fires, truncated }) => { if (live) setState({ loading: false, fires, truncated }); })
      .catch(() => { if (live) setState({ loading: false, failed: true }); });
    return () => { live = false; };
  }, [eventId]);

  const s = useMemo(() => summariseFires(state.fires), [state.fires]);
  const x = (t) => ((t - s.t0) / (s.t1 - s.t0)) * W;
  const H = Math.max(1, s.rows.length) * ROW_H + 18;

  return (
    <Box component="section" aria-label="Event fires over the last two years"
      sx={{ flexShrink: 0, borderTop: `1px solid ${tok('ink-08')}`, backgroundColor: tok('sunk'), px: 3, py: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' }, gap: 3, alignItems: 'center' }}>
      <Box>
        <Typography sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-50') }}>Fires · last 2 years · saved version</Typography>
        {state.loading && <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-50'), mt: 0.5 }}>Loading…</Typography>}
        {state.failed && <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('down'), mt: 0.5 }}>Couldn't load this event's fires.</Typography>}
        {!state.loading && !state.failed && (
          <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', lineHeight: 1.6, color: tok('text'), mt: 0.5 }}>
            {s.count === 0 ? 'No fires in the last two years.' : (
              <>
                Fired <strong>{s.count}{state.truncated ? '+' : ''}</strong> time{s.count === 1 ? '' : 's'}
                {s.medianGapDays !== null && <> · typically {Math.round(s.medianGapDays)} days apart on a symbol</>}
                <br />
                <Box component="span" sx={{ color: tok('ink-60') }}>Last {day(s.last.t)} on {s.last.symbol}</Box>
              </>
            )}
          </Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {s.count > 0 && (
          <svg viewBox={`0 0 ${W + 90} ${H}`} width="100%" height={H} role="img" aria-label={`${s.count} fires on a two-year timeline`} style={{ display: 'block' }}>
            {s.rows.map(([sym, fires], i) => {
              const y = i * ROW_H + ROW_H / 2;
              return (
                <g key={sym}>
                  <line x1={0} x2={W} y1={y} y2={y} style={{ stroke: tok('ink-12') }} />
                  {fires.map((f) => (
                    <rect key={f.t} x={x(f.t) - 1.5} y={y - 7} width={3} height={14} style={{ fill: tok('accent') }}>
                      <title>{`${sym} · ${day(f.t)}`}</title>
                    </rect>
                  ))}
                  <text x={W + 10} y={y + 4} fontSize="11" style={{ fill: tok('ink-60'), fontFamily: MONO }}>{sym}</text>
                </g>
              );
            })}
            {[0, 0.25, 0.5, 0.75, 1].map((p) => (
              <text key={p} x={p * W} y={H - 2} fontSize="10" textAnchor={p === 0 ? 'start' : p === 1 ? 'end' : 'middle'} style={{ fill: tok('ink-45'), fontFamily: MONO }}>
                {day(s.t0 + p * (s.t1 - s.t0))}
              </text>
            ))}
          </svg>
        )}
        {s.more > 0 && <Typography sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-50') }}>+{s.more} more symbols</Typography>}
        {s.count > 0 && (
          <Typography component={RouterLink} to={`/markets/${String(s.last.symbol).replace('/', '-')}`} sx={{ ...LABEL, fontSize: '0.5625rem', color: tok('accent'), textDecoration: 'none' }}>
            Open {s.last.symbol} chart →
          </Typography>
        )}
      </Box>
    </Box>
  );
}
